import {
	ComparisonMode,
	ReportDashboard,
	ReportDateRange,
	ReportRow,
	WidgetDisplay,
	WidgetSpec,
} from "../../../models/ReportBuilder";

/** Marks the JSON as ours; bumped if the shape ever changes incompatibly. */
export const DASHBOARD_EXPORT_FORMAT = "origo-dashboard/1";

/**
 * A dashboard's definition without anything tied to this organisation or
 * this user: no id, owner, sharing, visibility or filters (filters hold
 * customer/pool/contract ids that mean nothing elsewhere). Widget ids stay
 * so the rows can reference them; an import hands out fresh ones.
 */
export interface DashboardExport {
	format: typeof DASHBOARD_EXPORT_FORMAT;
	name: string;
	range: ReportDateRange;
	compare?: ComparisonMode;
	autoRefreshMinutes?: number;
	rows: ReportRow[];
	widgets: ExportedWidget[];
}

export interface ExportedWidget {
	id: string;
	spec: WidgetSpec;
	prompt: string;
	display?: WidgetDisplay;
	note?: string;
}

export const toDashboardExport = (d: ReportDashboard): DashboardExport => ({
	format: DASHBOARD_EXPORT_FORMAT,
	name: d.name,
	range: d.range,
	...(d.compare ? { compare: d.compare } : {}),
	...(d.autoRefreshMinutes ? { autoRefreshMinutes: d.autoRefreshMinutes } : {}),
	rows: d.rows,
	widgets: d.widgets.map((w) => ({
		id: w.id,
		spec: w.spec,
		prompt: w.prompt,
		...(w.display ? { display: w.display } : {}),
		...(typeof w.note === "string" ? { note: w.note } : {}),
	})),
});

/** The text that goes on the clipboard. */
export const serializeDashboard = (d: ReportDashboard): string =>
	JSON.stringify(toDashboardExport(d), null, 2);

const isRecord = (v: unknown): v is Record<string, unknown> =>
	typeof v === "object" && v !== null && !Array.isArray(v);

/**
 * Parses pasted text into a DashboardExport, or throws an Error whose message
 * says in Swedish what is wrong. Checks the shape the store relies on; the
 * widget specs are only checked for the fields a card needs to render, since
 * the backend validates the SQL again before any query runs.
 */
export function parseDashboardExport(text: string): DashboardExport {
	let raw: unknown;
	try {
		raw = JSON.parse(text);
	} catch {
		throw new Error("Texten är inte giltig JSON.");
	}
	if (!isRecord(raw)) throw new Error("JSON:en måste vara ett objekt.");
	if (raw.format !== DASHBOARD_EXPORT_FORMAT)
		throw new Error(
			`Det här är inte en exporterad dashboard (format saknas eller är inte ${DASHBOARD_EXPORT_FORMAT}).`
		);
	if (typeof raw.name !== "string" || !raw.name.trim()) throw new Error("Dashboarden saknar namn.");
	if (!isRecord(raw.range) || typeof raw.range.preset !== "string")
		throw new Error("Dashboarden saknar period (range.preset).");
	if (!Array.isArray(raw.widgets)) throw new Error("widgets måste vara en lista.");
	raw.widgets.forEach((w, i) => {
		if (!isRecord(w) || typeof w.id !== "string") throw new Error(`Widget ${i + 1} saknar id.`);
		if (!isRecord(w.spec) || typeof w.spec.sql !== "string" || !isRecord(w.spec.chart))
			throw new Error(`Widget ${i + 1} saknar spec med sql och chart.`);
	});
	if (raw.rows !== undefined && !Array.isArray(raw.rows))
		throw new Error("rows måste vara en lista.");

	const widgets = raw.widgets as Record<string, unknown>[];
	return {
		format: DASHBOARD_EXPORT_FORMAT,
		name: raw.name.trim(),
		range: raw.range as unknown as ReportDateRange,
		...(typeof raw.compare === "string" ? { compare: raw.compare as ComparisonMode } : {}),
		...(typeof raw.autoRefreshMinutes === "number"
			? { autoRefreshMinutes: raw.autoRefreshMinutes }
			: {}),
		rows: ((raw.rows ?? []) as unknown[]).filter(isRecord).map((r, i) => ({
			id: typeof r.id === "string" ? r.id : `row-${i}`,
			widgets: Array.isArray(r.widgets)
				? r.widgets.filter((w): w is string => typeof w === "string")
				: [],
		})),
		widgets: widgets.map((w) => ({
			id: w.id as string,
			spec: w.spec as unknown as WidgetSpec,
			prompt: typeof w.prompt === "string" ? w.prompt : "",
			...(isRecord(w.display) ? { display: w.display as WidgetDisplay } : {}),
			...(typeof w.note === "string" ? { note: w.note } : {}),
		})),
	};
}
